import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactTooltip from "react-tooltip";
import ReservationShowDetail from '../ReservationShowDetail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleDot } from '@fortawesome/free-regular-svg-icons'; //faLock
import { getToday, checkDayWithOther2Day } from '../../../utils/Date/SupportDate';
import './reservationSchedule.css'
import './dragDrop.css'
import { useSelector } from 'react-redux';
import { useDragDropSchedule } from '../../../hooks/useDragDropSchedule';
import { DragConfirmDialog, DragStatusIndicator } from './DragDropComponents';
import { canDragBooking } from '../../../utils/dragDropSchedule';
import { findElementBranch } from '../../../api/branch';
import useViewportMenuPosition from '../../../hooks/useViewportMenuPosition';
import { LIST_GUEST_ID_BLOCK } from '../../../constaints/listID/listID';

ReservationScheduleComponent.propTypes = {
};

// Schedule dates may include a time suffix, such as DD-MM-YYYY@HH:mm.
const toDateInput = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
        value = value.split('@')[0].trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
        const m = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
        if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const style = {
    backgroundColor: {
        oldDay: "rgba(211 211 211 / 50%)",
        newDay: "rgba(144 238 144 / 65%)"
    }

}

const hasBlockedGuest = booking => booking?.list_guest_id?.some(id => LIST_GUEST_ID_BLOCK.includes(id)) || false;

// Color palette (same as MakeRoomDataOtherDetails)
const LIST_COLOR_TEAM = {
    vat: ['red'],
    waiting: ['black'],
    hotel: ['gray'],
    OTA: [''],
    normal1: ["#ff5b5b", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#0000ff", "#9900ff", "#ff00ff"],
    normal2: ["#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#d0e0e3", "#cfe2f3", "#d9d2e9", "#ead1dc"],
    normal3: ["#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#9fc5e8", "#b4a7d6", "#d5a6bd"],
    normal4: ["#e06666", "#f6b26b", "#ffd966", "#93c47d", "#76a5af", "#6fa8dc", "#8e7cc3", "#c27ba0"],
    normal5: ["#ce3636", "#e69138", "#f1c232", "#6aa84f", "#45818e", "#3d85c6", "#674ea7", "#a64d79"],
    normal6: ["#990000", "#b45f06", "#bf9000", "#38761d", "#134f5c", "#0b5394", "#351c75", "#741b47"],
    normal7: ["#660000", "#783f04", "#7f6000", "#274e13", "#0c343d", "#073763", "#20124d", "#4c1130"]
};

const LIST_OTA_BRANCH = {
    "1": ["gray", "gray", "gray"],
    "3": ["#0000ff", "#0000ff", "#0000ff"],
    "4": ["gray", "gray", "gray"],
    "7": ["#ff9900", "#00ff00", "#9900ff", "#ffd966", "#ffd966"],
    "8": ["#00ff00", "#00ffff", "#ff00ff"],
    "9": ["gray", "gray", "gray"],
    "11": ["#00ff00", "#00ff00", "#00ff00"],
    "12": ["#00ff00", "#ff00ff", "#0000ff"],
    "13": ["#ff9900", "#00ff00", "#9900ff", "#00ffff", "#0000ff"],
    "15": ["#00ff00", "#00ffff", "#ff00ff", "#9900ff"],
    "16": ["#00ff00", "#ff00ff", "#0000ff", "#00ffff"],
    "97": [],
    "98": [],
    "99": ["#00ff00", "#ff00ff", "#0000ff", "#00ffff"],
};

const OTA_NAMES_BRANCH = {
    "1": ["AGODA", "TRIP.COM", "HafH"],
    "3": ["EXPEDIA", "BOOKING.COM", "AGODA"],
    "4": ["AGODA", "TRIP.COM", "HafH"],
    "7": ["EXPEDIA", "BOOKING.COM", "AGODA", "RAKUTEN", "HafH"],
    "8": ["EXPEDIA", "BOOKING.COM", "AGODA"],
    "9": ["AGODA", "TRIP.COM", "HafH"],
    "11": ["EXPEDIA", "AGODA", "TRIP.COM"],
    "12": ["EXPEDIA", "BOOKING.COM", "AGODA"],
    "13": ["EXPEDIA", "BOOKING.COM", "AGODA", "RAKUTEN", "TRIP.COM"],
    "15": ["EXPEDIA", "BOOKING.COM", "AGODA", "TRIP.COM"],
    "16": ["EXPEDIA", "BOOKING.COM", "AGODA", "TRIP.COM"],
    "99": ["EXPEDIA", "BOOKING.COM", "AGODA", "TRIP.COM"],
};

// Danh sách cấu hình tất cả các icon có thể xuất hiện
const ICON_DEFINITIONS = [
    {
        id: "star",
        condition: (room) => room.userId && room.userId !== "",
        iconClass: "fa-solid fa-star",
        side: "left",
        fixedPosition: 1, // Icon này luôn nằm ở vị trí số 1 bên trái
    },
    {
        id: "lock",
        condition: (room, listRender) => room.move && listRender === "listReservation",
        iconClass: "fa-solid fa-lock",
        side: "left",
    },
    {
        id: "prePay",
        condition: (room) => room.payment === "Pre-Pay",
        iconClass: "fa-solid fa-sack-dollar",
        side: "left",
    },
    {
        id: "rentCar",
        condition: (room) => room.rentCar,
        iconClass: "fa-solid fa-car",
        side: "left",
    },
    {
        id: "pickUp",
        condition: (room) => room.pickUp !== "",
        iconClass: "fa-solid fa-plane",
        side: "left",
    },
    {
        id: "breakfast",
        condition: (room) => room.other1?.toLowerCase().includes("non breakfast") || room.other1?.toLowerCase().includes("no breakfast"),
        iconClass: "fa-solid fa-bowl-rice icon-off",
        side: "left",
    },
    {
        id: "dropOff",
        condition: (room) => room.dropOff !== "",
        iconClass: "fa-solid fa-plane",
        side: "right",
        fixedPosition: 1, // Luôn nằm ở vị trí số 1 bên phải
    },
    

    // {
    //   id: "wifi",
    //   condition: (room) => room.hasWifi === true,
    //   iconClass: "fa-solid fa-wifi",
    //   side: "left" // Tự động xếp vị trí tiếp theo bên trái
    // }
];

const renderSmartIcons = (room, listRender) => {
    // console.log("room", room)
    // if(room.id === "08ef2cf7b5") {
    //     console.log("room", room)
    // }
   

  const activeIcons = ICON_DEFINITIONS.filter(icon => icon.condition(room, listRender));
    
  let leftDynamicIndex = 1;
  let rightDynamicIndex = 1;

  return activeIcons.map((icon) => {
    let position;

    // 3. Tính toán vị trí thông minh
    if (icon.fixedPosition) {
      position = icon.fixedPosition; // Nếu có vị trí cố định thì dùng luôn
    } else {
      // Nếu không có vị trí cố định, tự động tăng số thứ tự (1, 2, 3...) theo từng bên
      if (icon.side === "left") {
        // Mẹo: Nếu vị trí số 1 đã bị chiếm bởi icon 'star' (fixedPosition: 1), 
        // icon động đầu tiên sẽ tự nhảy sang vị trí số 2
        if (leftDynamicIndex === 1 && activeIcons.some(i => i.side === "left" && i.fixedPosition === 1)) {
          leftDynamicIndex = 2;
        }
        position = leftDynamicIndex++;
      } else {
        if (rightDynamicIndex === 1 && activeIcons.some(i => i.side === "right" && i.fixedPosition === 1)) {
          rightDynamicIndex = 2;
        }
        position = rightDynamicIndex++;
      }
    }

    // 4. Trả về thẻ HTML hoàn chỉnh với class vị trí động
    return (
      <i
        key={icon.id}
        className={`${icon.iconClass} show-small-icon_${icon.side}${position}`}
      />
    );
  });
};

function ReservationScheduleComponent(props) {
    const { data, selectRoomType, listRender, onFinish, renderNumber,showComponent, newRealtimeCells = [], newRealtimeBookingIds = [], sharedBookingTooltip = false } = props;

    const sharedTooltipId = `reservation-tooltip-${listRender}-${renderNumber || 'all'}`;
    const tooltipBookings = new Map();

    function bookingTooltipProps(booking, roomNumber, index, arrayCell = false) {
        const bookingKey = "data" + roomNumber + booking.id + index;
        if (sharedBookingTooltip) {
            if (!arrayCell || !show) tooltipBookings.set(bookingKey, booking);
            return {
                'data-tip': !dragState.isDragging && (!arrayCell || !show) ? bookingKey : undefined,
                'data-for': sharedTooltipId,
                'data-class': arrayCell ? '' : 'reservation-shared-tooltip bg_white'
            };
        }
        return {
            'data-tip': !dragState.isDragging ? true : undefined,
            'data-for': bookingKey
        };
    }

    const [show, setShow] = useState(false);
    const [quickMenuRect, setQuickMenuRect] = useState(null);
    const [id, setID] = useState("");
    const [, setPoints] = useState({ x: 0, y: 0 });
    const isCheckMode = false; //true;// 
    const ruleRenderNumber = 15;

    const auth = useSelector(state => state.auth);
    const { branchID } = auth.user;
    const tokenFromStore = useSelector(state => state.token);
    const token = tokenFromStore || localStorage.getItem('token') || '';
    const dateLoad = useSelector(state => state.dataPickUp.reservation);
    const { month } = dateLoad;
    const [isHoverKeep, setIsHoverKeep] = useState(false);
    const [quickKeepSignature, setQuickKeepSignature] = useState(null);
    const [quickKeepSignatureError, setQuickKeepSignatureError] = useState('');
    const [dataQuickKeep, setDataQuickKeep] = useState({
        type: "keep",
        night:"",
        note: "",
        maker:""
    })

    // === Drag & Drop State ===
    const [dragStatus, setDragStatus] = useState(null);
    const [bookingMenu, setBookingMenu] = useState(null);
    const [menuStep, setMenuStep] = useState(null); // null | 'confirm' | 'cancel' | 'color' | 'extend_shorten'
    const [cancelRequirement2, setCancelRequirement2] = useState('');
    const [pickedColor, setPickedColor] = useState('');
    const [extendShortenNights, setExtendShortenNights] = useState('');
    const [scheduleModal, setScheduleModal] = useState(null); // { booking, form }
    const [scheduleError, setScheduleError] = useState('');
    const [scheduleSaving, setScheduleSaving] = useState(false);
    const [cancelledBookingVisuals, setCancelledBookingVisuals] = useState([]);
    useEffect(() => {
        if (cancelledBookingVisuals.length === 0) return;
        const timer = window.setTimeout(() => setCancelledBookingVisuals([]), 750);
        return () => window.clearTimeout(timer);
    }, [cancelledBookingVisuals]);
    const {
        dragState,
        confirmDialog,
        startDrag,
        handleDragOver,
        handleDrop,
        cancelDrag,
        confirmDragDrop
    } = useDragDropSchedule({
        data,
        branchID,
        auth,
        month,
        onFinish
    });
    // === End Drag & Drop State ===

    useEffect(() => {
        if (sharedBookingTooltip && !dragState.isDragging) ReactTooltip.rebuild();
    }, [sharedBookingTooltip, data, selectRoomType, listRender, renderNumber, show, dragState.isDragging]);

    useEffect(() => {
        if(!isCheckMode) {
            const handleClick = () => { setShow(false); setBookingMenu(null); setMenuStep(null); };
            window.addEventListener('click', handleClick);
            return () => window.removeEventListener('click', handleClick);
        }
    }, [isCheckMode]);

    const isQuickMenuOpen = show && branchID === 99 && quickMenuRect !== null;
    const isBookingMenuOpen = !!bookingMenu;
    const spotlightRect = bookingMenu?.rect || (isQuickMenuOpen ? quickMenuRect : null);
    const quickMenuPlacement = useViewportMenuPosition(
        isQuickMenuOpen, quickMenuRect?.left, quickMenuRect?.bottom + 6, quickMenuRect?.top - 6
    );
    const bookingMenuPlacement = useViewportMenuPosition(!!bookingMenu, bookingMenu?.x, bookingMenu?.y);
    const colorMenuPlacement = useViewportMenuPosition(
        !!bookingMenu && !hasBlockedGuest(bookingMenu.booking) && menuStep === 'color',
        bookingMenuPlacement.position.left + 200 + 190 > window.innerWidth
            ? bookingMenuPlacement.position.left - 190
            : bookingMenuPlacement.position.left + 204,
        bookingMenuPlacement.position.top
    );

    // Keep the selected cell aligned with the spotlight while either menu is open.
    useEffect(() => {
        if (!isBookingMenuOpen && !isQuickMenuOpen) return;

        // Remember scroll position of the page and every scrollable container
        const saved = [];
        const root = document.scrollingElement || document.documentElement;
        saved.push({ el: root, left: root.scrollLeft, top: root.scrollTop });
        document.querySelectorAll('*').forEach(el => {
            if (el.closest('.right_click_menu')) return;
            if (el.scrollHeight <= el.clientHeight && el.scrollWidth <= el.clientWidth) return;
            const o = window.getComputedStyle(el);
            if (/(auto|scroll)/.test(o.overflow + o.overflowX + o.overflowY)) {
                saved.push({ el, left: el.scrollLeft, top: el.scrollTop });
            }
        });

        const restore = () => saved.forEach(({ el, left, top }) => {
            if (el.scrollLeft !== left) el.scrollLeft = left;
            if (el.scrollTop !== top) el.scrollTop = top;
        });
        const block = (e) => {
            if (!e.target.closest('.right_click_menu')) e.preventDefault();
        };
        const blockKeys = (e) => {
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)
                && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName)) {
                e.preventDefault();
            }
        };

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('wheel', block, { passive: false });
        window.addEventListener('touchmove', block, { passive: false });
        window.addEventListener('keydown', blockKeys);
        // scroll doesn't bubble: capture it to undo scrollbar dragging / programmatic scroll
        window.addEventListener('scroll', restore, true);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('wheel', block);
            window.removeEventListener('touchmove', block);
            window.removeEventListener('keydown', blockKeys);
            window.removeEventListener('scroll', restore, true);
        };
    }, [isBookingMenuOpen, isQuickMenuOpen]);

    useEffect(() => {
        if (!dragState.isDragging) return;

        const handleGlobalContextMenu = (e) => {
            // Cell handlers manage the 3-stage right click cycle
            e.preventDefault();
        };

        window.addEventListener('contextmenu', handleGlobalContextMenu, true);
        return () => window.removeEventListener('contextmenu', handleGlobalContextMenu, true);
    }, [dragState.isDragging, cancelDrag]);

    const numbers = !branchID ? 0 : listRender === "listReservation"
        ? renderNumber === 1 ? ruleRenderNumber - 1
            : data.listNumberRoom.length > ruleRenderNumber ? data.listNumberRoom.length - 1 : 0
        : Math.max(0, Object.keys(data[listRender]).length - 1);
    useEffect(() => {
        if (numbers === ruleRenderNumber - 1 && listRender === "listReservation" && renderNumber === 1) {
            onFinish();
        }  
        else if(renderNumber === 2 && listRender ==="listReservation" && numbers === data.listNumberRoom.length - 1) {
            onFinish();
        } 
        else if (listRender === "listWaiting" && (numbers === Object.keys(data[listRender]).length - 1 || Object.keys(data[listRender]).length === 0)) {
            onFinish();
        } 
           
      }, [onFinish, numbers, listRender, renderNumber, data]);

    useEffect(() => {
        if (!show || branchID !== 99) return;

        let active = true;
        setQuickKeepSignature(null);
        setQuickKeepSignatureError('');

        findElementBranch(data.branchID, 'signature', token)
            .then(({ data: response }) => {
                if (active) setQuickKeepSignature(response.signature);
            })
            .catch(error => {
                if (!active) return;
                console.error('Failed to load branch RC list:', error);
                setQuickKeepSignatureError('Unable to load RC list. Please reopen the menu to retry.');
            });

        return () => { active = false; };
    }, [show, branchID, data.branchID, token]);

    function onChangeDataQucikKeep(target, value) {
        setDataQuickKeep(previous => ({ ...previous, [target]: value }));
    }

    function checkRoomWithStyle(room) {
        var result;
        const listKeyOnListRoom = Object.keys(data.listRoom)
        listKeyOnListRoom.forEach(roomCheck => {
            if (data.listRoom[roomCheck].includes(room)) result = roomCheck;
        })
        return result;
    }

    function renderSchedule() {
        var result = [];

        // render 
        if(listRender === "listReservation") { 
            if(renderNumber === 1) {
                for (let i = 0; i < ruleRenderNumber; i++) {
    
                    if (selectRoomType === checkRoomWithStyle(data.listNumberRoom[i]) || selectRoomType === "All") {
                        result.push(<div className="d-flex rsv-ca ">
                            {renderRoom(data.listNumberRoom[i])}
                        </div>)
                    }

                }
            } else {
                for (let j = ruleRenderNumber; j < data.listNumberRoom.length; j++) {
    
                    if (selectRoomType === checkRoomWithStyle(data.listNumberRoom[j]) || selectRoomType === "All") {
                        result.push(<div className="d-flex rsv-ca ">
                            {renderRoom(data.listNumberRoom[j])}
                        </div>)
                    }
                }
            }

        } else {
            const listKey = Object.keys(data[listRender]);

            for (let i = 0; i < listKey.length; i++) {
                if (selectRoomType === checkRoomWithStyle(listKey[i]) || selectRoomType === "All") {
                    result.push(<div className="d-flex rsv-ca ">
                        {renderRoom(listKey[i])}
                    </div>)
                }
            }
        }

        return result;
    };

    // Right click cycle on a booking: 1 = drag mode, 2 = support menu, 3 = back to normal
    function handleBookingContextMenu(e, roomNumber, i, booking) {
        setShow(false);
        if (bookingMenu && bookingMenu.booking.id === booking.id) {
            setBookingMenu(null);
            setMenuStep(null);
            return;
        }
        if (booking.move === true || dragState.isDragging) {
            if (dragState.isDragging) cancelDrag();
            setMenuStep(null);
            setBookingMenu({ booking, roomNumber, i, x: e.clientX, y: e.clientY, rect: e.currentTarget?.getBoundingClientRect?.() });
            return;
        }
        setBookingMenu(null);
        startDrag(e, roomNumber, i, booking);
    }

    function onBookingMenuAction(action) {
        const b = bookingMenu?.booking;
        const room = bookingMenu?.roomNumber;
        if (!b) return;
        if (action === 'confirm') { setMenuStep('confirm'); return; }
        if (action === 'color') {
            if (hasBlockedGuest(b)) return;
            setPickedColor(b.color || '');
            setMenuStep('color');
            return;
        }
        if (action === 'extend_shorten') {
            setExtendShortenNights(String(b.nights ?? ''));
            setMenuStep('extend_shorten');
            return;
        }
        if (action === 'cancel') {
            setCancelRequirement2(b.other2 || '');
            setMenuStep('cancel');
            return;
        }
        setBookingMenu(null);
        setMenuStep(null);
        if (action === 'schedule') {
            setScheduleError('');
            setScheduleModal({
                booking: b,
                roomNumber: room,
                form: {
                    checkin: toDateInput(b.checkin),
                    checkout: toDateInput(b.checkout),
                    nights: b.nights ?? '',
                    other2: b.other2 || ''
                }
            });
            return;
        }
        // open: open booking edit page
        window.open(`/room/${data.branchID}/${b.id}`, "_blank");
    }

    function closeBookingMenu() {
        setBookingMenu(null);
        setMenuStep(null);
    }

    // TODO: chờ implement - Confirm change color
    function handleConfirmColor(booking, color) {
        const changeColorByQuickMenu = async () => {
            try {
                // TODO: Update API endpoint if different
                const response = await fetch(`/booking/${branchID}/move-booking/change_color_by_quick_menu`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token || ''
                    },
                    body: JSON.stringify({
                        bookingId: booking.id,
                        sourceRoom: booking.roomSelect,
                        targetRoom: booking.roomSelect,
                        sourceDate: booking.checkout,
                        targetDate: booking.checkout,//toDateInput(booking.checkin) || new Date().toISOString().slice(0, 10), // YYYY-MM-DD (server tu tinh checkout theo nightsNewChange)
                        branchID: branchID,
                        userId: auth.user.id,
                        colorNewChange: color,
                        logs: [{"action": "Change color by quick menu", "userId": auth.user.id, "timestamp": new Date().toISOString()}],
                        request2: "" 
                    })
                });
                const result = await response.json();
                if (!response.ok) {
                    console.error('Failed to change color booking:', result?.message || result?.msg || `Request failed (${response.status})`);
                    return;
                }
                if (result.success) {
                    console.log('Booking color changed successfully');
                } else {
                    console.error('Failed to change color booking:', result.message || 'Unknown error');
                }
            } catch (error) {
                console.error('Error changing color booking:', error);
            }
        };
        changeColorByQuickMenu();
        closeBookingMenu();

    }

    function renderColorPalette() {
        const otaColors = LIST_OTA_BRANCH["" + branchID] || [];
        const otaNames = OTA_NAMES_BRANCH["" + branchID] || [];
        return Object.keys(LIST_COLOR_TEAM)
            .filter(label => label !== 'OTA' || otaColors.length > 0)
            .map(label => {
                const colors = label === 'OTA' ? otaColors : LIST_COLOR_TEAM[label];
                return (
                    <div className="rcm-color-row" key={label} title={label.toUpperCase()}>
                        <div className="rcm-color-list">
                            {colors.map((c, idx) => (
                                <span
                                    key={idx}
                                    title={label === 'OTA' ? (otaNames[idx] || '') : c}
                                    className={`rcm-swatch ${pickedColor === c ? 'selected' : ''}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setPickedColor(c)}
                                >{label === 'OTA' ? (otaNames[idx] || '').charAt(0) : ''}</span>
                            ))}
                        </div>
                    </div>
                );
            });
    }

    // TODO: chờ implement - Confirm change

    function handleConfirmSchedule(booking, form)   {
        if (scheduleSaving) return;
        setScheduleSaving(true);
        setScheduleError('');
        const changeSchedule = async () => {
            try {
                // TODO: Implement API call for changing schedule
                console.log('Changing schedule for booking:', booking?.id, 'with form:', form);
                const response = await fetch(`/booking/${branchID}/move-booking/change_schedule`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token || ''
                    },
                    body: JSON.stringify({
                        bookingId: booking.id,
                        checkin: form.checkin,
                        checkout: form.checkout,
                        nights: form.nights,
                        branchID: branchID,
                        userId: auth.user.id,
                        logs: [{"action": "Change schedule", "userId": auth.user.id, "timestamp": new Date().toISOString()}],
                        request2: form.other2
                    })
                });
                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result?.message || result?.msg || `Request failed (${response.status})`);
                }
                if (result.success) {
                    setScheduleModal(null);
                } else {
                    throw new Error(result.message || 'Unable to change schedule');
                }
            } catch (error) {
                console.error('Error changing schedule:', error);
                setScheduleError(error.message);
            } finally {
                setScheduleSaving(false);
            }
        };  
        changeSchedule();
    }

    // TODO: chờ implement - Confirm change schedule
    function handleConfirmCancel(booking, requirement2){
        const visuals = Array.from(document.querySelectorAll('[data-booking-id]'))
            .filter(element => element.dataset.bookingId === String(booking.id))
            .map(element => {
                const rect = element.getBoundingClientRect();
                const computed = window.getComputedStyle(element);
                return {
                    left: rect.left, top: rect.top, width: rect.width, height: rect.height,
                    backgroundColor: computed.backgroundColor, color: computed.color,
                    text: element.textContent
                };
            });
        const cancelBooking = async () => {
            try {
                // TODO: Update API endpoint if different
                const response = await fetch(`/booking/${branchID}/move-booking/cancel_by_quick_menu`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token || ''
                    },
                    body: JSON.stringify({
                        bookingId: booking.id,
                        sourceRoom: booking.roomSelect,
                        targetRoom: booking.roomSelect,
                        sourceDate: booking.checkout,
                        targetDate: toDateInput(booking.checkin) || new Date().toISOString().slice(0, 10), // YYYY-MM-DD (server tu tinh checkout theo nightsNewChange)
                        branchID: branchID,
                        userId: auth.user.id,
                        logs: [{"action": "Cancel by quick emnu", "userId": auth.user.id, "timestamp": new Date().toISOString()}],
                        request2: requirement2 
                    })
                });
                const result = await response.json();
                if (!response.ok) {
                    console.error('Failed to cancel booking:', result?.message || result?.msg || `Request failed (${response.status})`);
                    return;
                }
                if (result.success) {
                    setCancelledBookingVisuals(visuals);
                    console.log('Booking canceled successfully');
                } else {
                    console.error('Failed to cancel booking:', result.message || 'Unknown error');
                }
            } catch (error) {
                console.error('Error canceling booking:', error);
            }
        };
        cancelBooking();
        closeBookingMenu();
    }

    // TODO: chờ implement - Confirm extend/shorten nights
    function handleConfirmExtendShorten(booking, nights) {        
        const changeNight = async () => {
            try {
                // TODO: Update API endpoint if different
                const response = await fetch(`/booking/${branchID}/move-booking/extend_shorten`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token || ''
                    },
                    body: JSON.stringify({
                        bookingId: booking.id,
                        sourceRoom: booking.roomSelect,
                        targetRoom: booking.roomSelect,
                        sourceDate: booking.checkout,
                        targetDate: toDateInput(booking.checkin) || new Date().toISOString().slice(0, 10), // YYYY-MM-DD (server tu tinh checkout theo nightsNewChange)
                        branchID: branchID,
                        nightsNewChange: nights,
                        userId: auth.user.id,
                        logs: [{"action": booking.nights - nights > 0 ? "shorten" : "extend", "userId": auth.user.id, "timestamp": new Date().toISOString(), nights: nights}],
                        request2: booking.request2 // <--- Truyền biến này vào request2 ở đây
                    })
                });
                const result = await response.json();
                if (!response.ok) {
                    console.error('Failed to change nights:', result?.message || result?.msg || `Request failed (${response.status})`);
                    return;
                }
                if (result.success) {
                    console.log('Nights changed successfully');
                } else {
                    console.error('Failed to change nights:', result.message || 'Unknown error');
                }
            } catch (error) {
                console.error('Error changing nights:', error);
            }
        };
        changeNight();
        closeBookingMenu();
    }

    function onContextMenu(e, roomNumber, i) {
        e.preventDefault();
        e.stopPropagation();

        const booking = data[listRender]?.[roomNumber]?.[i];

        // Booking: 3-stage right click cycle
        if (booking && typeof booking === 'object' && booking.id) {
            handleBookingContextMenu(e, roomNumber, i, booking);
            return;
        }

        setBookingMenu(null);
        if (dragState.isDragging) {
            cancelDrag();
            setShow(false);
            return;
        }
        
        if (show && id === roomNumber + i) {
            setShow(false);
            return;
        }

        // Old behavior: Show context menu for empty cells
        setIsHoverKeep(false);
        setQuickMenuRect(e.currentTarget.getBoundingClientRect());
        setID(roomNumber + i);
        setPoints({ x: e.pageX, y: e.pageY }); 
        setShow(true);
    }

    function makeRealtimeCellKey(roomNumber, index) {
        return `${roomNumber}__${data.month}-${String(index + 1).padStart(2, '0')}`;
    }

    // === Drag & Drop Confirmation Handler ===
    const handleConfirmDragDrop = async (dataChangeOther2, type) => {
        if (!token) {
            setDragStatus('error');
            return;
        }

        const success = await confirmDragDrop(async (moveData) => {
            try {
                // TODO: Update API endpoint if different
                const response = await fetch(`/booking/${branchID}/move-booking/${type}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token || ''
                    },
                    body: JSON.stringify({
                        bookingId: moveData.bookingId,
                        sourceRoom: moveData.sourceRoom,
                        targetRoom: moveData.targetRoom,
                        sourceDate: moveData.sourceDate,
                        targetDate: moveData.targetDate,
                        branchID: branchID,
                        userId: auth.user.id,
                        logs: moveData.logs,
                        request2: dataChangeOther2 // <--- Truyền biến này vào request2 ở đây
                    })
                });

                const result = await response.json();

                // console.log("✅ Move Booking Response:", result);

                if (!response.ok) {
                    setDragStatus('error');
                    return {
                        success: false,
                        message: result?.message || result?.msg || `Request failed (${response.status})`
                    };
                }

                if (result.success) {
                    setDragStatus('success');
                    setTimeout(() => {
                        setDragStatus(null);
                    }, 1200);
                    return { success: true, message: 'Booking moved successfully' };
                } else {
                    setDragStatus('error');
                    return { success: false, message: result.message || 'Failed to move booking' };
                }
            } catch (error) {
                console.error('Error moving booking:', error);
                setDragStatus('error');
                return { success: false, message: 'An error occurred while moving booking' };
            }
        });

        if (!success) {
            setDragStatus('error');
        }
    };
    // === End Confirmation Handler ===

    function renderRoom(roomNumber) {
        var result = [];
        function distributionRoomEmpty(dataShorten, dataExtend, index, dataRoom) {
            //console.log("dataRoom", dataRoom)
            // console.log("dataExtend", dataExtend)
            // console.log("dataShorten", dataShorten)
            // console.log("index", index)

            if(index === 0) {
                if(dataExtend === "" && index !== (data.listShowDate.length - 1)) {
                    return "rsv-room";
                }

                if(dataExtend !== "") {
                    if(dataExtend.timeEarly.slice(0,2) < 15) return "rsv-room_half"
                    else return "rsv-room";
                }
            } else {

                if (dataExtend !=="" && dataShorten !== "" && dataExtend && dataShorten) {
                    if("timeEarly" in dataExtend && "timeLateOut" in dataShorten ) {
                        if (index > 0 
                           && Number(dataExtend.timeEarly.slice(0, 2)) < 15 
                            && dataShorten.timeLateOut !== "12:00") {
                                if(dataShorten.nights ===0) return "rsv-room_half"
                                return "op-hidden"
                        }
                        
                    }
                }

                if (index > 0 && dataShorten !== "" && dataShorten.timeLateOut !== "12:00"  
                    && Number(dataShorten.timeLateOut.slice(0,2)) > 12 
                    && dataShorten.nights !==0) {
                    if(dataExtend !== "" && dataExtend !== undefined) {
                        if(Number(dataExtend.timeEarly.slice(0, 2)) < 15 ) {
                            // console.log("out hide")
                            return "rsv-room";
                        } else {
                            return "rsv-room_half"
                        }
                    } else {
                        return "rsv-room_half"
                    };
                    }
                else if (index > 0 && index !== (data.listShowDate.length - 1)
                        && dataExtend !== "" && Number(dataExtend.timeEarly.slice(0, 2)) < 15 //&& dataShorten.timeLateOut !== "12:00" 
                        && dataExtend.nights ===0 ) {
                    //console.log("out hide")
                    return "rsv-room_half";
                }
                else if (index > 0 && index !== (data.listShowDate.length - 1)
                    && dataExtend !== "" && Number(dataExtend.timeEarly.slice(0, 2)) < 15 && dataExtend.nights !==0 ) {
                    return "rsv-room_half";
                }
                // else if (index > 0 && Number(dataExtend.timeEarly.slice(0, 2)) < 15 && dataShorten.timeLateOut !== "12:00" ) {
                //     return ""
                // }
                else {
                    if(index > 0 && dataShorten !=="") {
                        // if(dataShorten.id ==="99c0997db4") { 

                        //     console.log("vo here distributionRoomEmpty", dataShorten.timeLateOut)
                        // }
                        
                        // if(dataShorten.timeLateOut !== "12:00") return "rsv-room_half"
                        // // if("id" in dataShorten){
                        //     if(dataShorten.id ==="99c0997db4") {
                        //         console.log("vo here distributionRoomEmpty")
                        //     }
                        // }
                    }

                    return "rsv-room";
                }
            }}

        const today = getToday();
        const dataRoom = data[listRender] === undefined ? undefined : data[listRender][roomNumber];


        if (dataRoom) {
            for (let i = 0; i <= (data.listShowDate.length - 1); i++) {
                const checkMonth = checkDayWithOther2Day(today, (data.month + "-" + String(i + 1).padStart(2, '0')));
                const canNotEdit = checkMonth === 1 || listRender ==="listCancel" || listRender ==="listWaiting"
                const isRealtimeNewCell = listRender === "listReservation" && newRealtimeCells.includes(makeRealtimeCellKey(roomNumber, i));
                const isRealtimeNewBooking = !Array.isArray(dataRoom[i]) && dataRoom[i] && typeof dataRoom[i] === 'object' && dataRoom[i].id && newRealtimeBookingIds.includes(dataRoom[i].id);
          
                if (dataRoom[i] === "") {

                    result.push(<div
                        className={`${distributionRoomEmpty(
                            Array.isArray(dataRoom[i - 1]) ? dataRoom[i - 1][dataRoom[i - 1].length - 1] : dataRoom[i - 1], 
                            Array.isArray(dataRoom[i + 1]) ? dataRoom[i + 1][0] : dataRoom[i + 1], 
                            i,
                            dataRoom)} ${isRealtimeNewCell ? 'rsv-cell-realtime-new' : ''}`}
                        id={`cell-${roomNumber}-${i}`}
                        style={{ 
                            backgroundColor: canNotEdit ? style.backgroundColor.oldDay : "",
                            cursor: dragState.isDragging ? 'copy' : 'default'
                        }}
                        onClick={canNotEdit ? ()=>{} : (e) => {
                            if (dragState.isDragging) {
                                handleDrop(e, roomNumber, i);
                                return;
                            }
                            window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")
                        }}
                        //onContextMenu={(e) => onContextMenu(e)}
                        onContextMenu={(e) => onContextMenu(e, roomNumber, i)}
                        onDragOver={(e) => {
                            if (dragState.isDragging) {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                handleDragOver(e, roomNumber, i);
                            }
                        }}
                        onDrop={(e) => {
                            if (dragState.isDragging) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDrop(e, roomNumber, i);
                            }
                        }}
                        onDragLeave={(e) => {
                            e.currentTarget?.classList.remove('drop-zone-valid', 'drop-zone-invalid', 'drop-zone-processing');
                        }}
                    >
                        {/* {String(i + 1).padStart(2, '0')} */}
                        {(isCheckMode ? "X1" : "")+String(i + 1).padStart(2, '0')}
                        {
                            listRender === "listReservation" && (i >= 16 && i <= 21 && month ==="2026-02") && (branchID === 99) &&
                            <div  >
                                <img src="https://cdn-icons-png.flaticon.com/512/3918/3918628.png" alt=""
                                    style={{
                                        width: "40px",
                                        height: "43px",
                                        position: "absolute",
                                        top: "3px",
                                        left: "30px",
                                        opacity: 0.4
                                    }}  />

                                    <div style={{
                                        position: "absolute",
                                        top: "13px",
                                        left: "40px",
                                        fontWeight: "bold",
                                        color: "yellow",}}>Tết</div>
                                </div>

                            //<span style={{background:"#bd060d", color:"yellow", padding: "4px", borderRadius:'30%'}}>TẾT</span>
                            // <img src="https://azumayavietnam.com/image/logo/tet.png" alt="tet" width="40"/>
                        }
                        
                        
                        {branchID === 99 && id === (roomNumber + i) && !canNotEdit && show && createPortal(
                            <div className="right_click_menu quick-room-menu"
                                ref={quickMenuPlacement.ref}
                                style={{ ...quickMenuPlacement.position, zIndex: 2147483647 }}
                                aria-label={`Room ${roomNumber} actions`}
                                onClick={(e) => e.stopPropagation()}
                                onContextMenu={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShow(false);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Escape') {
                                        e.stopPropagation();
                                        setShow(false);
                                    }
                                }}
                            >
                                <div className="quick-room-menu-header">
                                    <strong>Room {roomNumber}</strong>
                                    <span className='ml-2'>{`(${data.month}-${String(i + 1).padStart(2, '0')})`}</span>
                                </div>
                                <ul className="quick-room-menu-list">
                                    <li>
                                        <button type="button" className="quick-room-menu-action"
                                            onClick={() => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}>
                                            Open Full Edit
                                            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                                        </button>
                                    </li>
                                    <li className="quick-room-menu-section">
                                        <button type="button" className="quick-room-menu-action"
                                            aria-expanded={isHoverKeep}
                                            aria-controls="quickKeepForm"
                                            onClick={() => setIsHoverKeep(previous => !previous)}>
                                            Make Keep / Lock
                                            <i className={`fa-solid fa-chevron-${isHoverKeep ? 'up' : 'down'}`} aria-hidden="true" />
                                        </button>

                                        {isHoverKeep && (
                                            <div id="quickKeepForm" className="quick-room-form">
                                                <div className="quick-room-type" role="group" aria-label="Room status">
                                                    <div className="quick-room-type-option">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="radio" 
                                                            name="keepLookOption" 
                                                            id="radioKeep" 
                                                            value="keep" 
                                                            checked={dataQuickKeep.type === "keep"} 
                                                            onChange={e => onChangeDataQucikKeep("type", e.target.value)}
                                                        />
                                                        <label htmlFor="radioKeep">
                                                            Keep
                                                        </label>
                                                    </div>
                                                    <div className="quick-room-type-option">
                                                        <input 
                                                            className="form-check-input" 
                                                            type="radio" 
                                                            name="keepLookOption" 
                                                            id="radioLook" 
                                                            value="lock"
                                                            checked={dataQuickKeep.type === "lock"} 
                                                            onChange={e => onChangeDataQucikKeep("type", e.target.value)}
                                                        />
                                                        <label htmlFor="radioLook">
                                                            Lock
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="quick-room-fields">
                                                    <div className="quick-room-field">
                                                        <label htmlFor="quickKeepNights">Nights:</label>
                                                        <input id="quickKeepNights" type='number' className="form-control form-control-sm" placeholder="Nights..."
                                                            value={dataQuickKeep.night}
                                                            onChange={e => onChangeDataQucikKeep("night", e.target.value)}/>
                                                    </div>
                                                    <div className="quick-room-field">
                                                        <label htmlFor="quickKeepMaker">Maker:</label>
                                                        <select id="quickKeepMaker" className="form-control form-control-sm"
                                                            value={dataQuickKeep.maker}
                                                            disabled={!quickKeepSignature || !!quickKeepSignatureError}
                                                            onChange={e => onChangeDataQucikKeep("maker", e.target.value)}>
                                                            <option value="">{!quickKeepSignature && !quickKeepSignatureError ? 'Loading RC list...' : 'Select Maker'}</option>
                                                            {quickKeepSignature && quickKeepSignature.list.map((name, index) => (
                                                                name !== "" && quickKeepSignature.select.includes(index) && (
                                                                    <option key={index} value={name}>{name}</option>
                                                                )
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                {quickKeepSignatureError && (
                                                    <div className="quick-room-error" role="alert">{quickKeepSignatureError}</div>
                                                )}
                                                <div className="quick-room-field">
                                                    <label htmlFor="quickKeepNote">Note:</label>
                                                    <textarea id="quickKeepNote" rows={2} className="form-control form-control-sm" placeholder="Note..."
                                                    value={dataQuickKeep.note}
                                                      onChange={e => onChangeDataQucikKeep("note", e.target.value)}></textarea>
                                                </div>
                                                <button type="button" className='btn btn-primary btn-sm w-100 quick-room-confirm'
                                                    onClick={() => {
                                                        setShow(false);
                                                        setDataQuickKeep({night:"",maker:"",note:"",type:"keep"})
                                                        props.callApiQuickMakeRoom(dataQuickKeep, roomNumber, `${data.month}-${String(i + 1).padStart(2, '0')}`)}
                                                    }>Confirm</button>
                                            </div>
                                        )}
                                    </li>
                                </ul>
                            </div>,
                            document.body
                        )}
                    </div>)
                }
                else if (!Array.isArray(dataRoom[i])) {
                    if ("name" in dataRoom[i]) {
                        //console.log("------------------------")
                        const lengthRoom = dataRoom[i].nights;
                        const checkMonthStartMonth = (i === 0 && checkDayWithOther2Day(dataRoom[i].checkin, (data.month + "-01")) === 1);
                        const lateOut = (dataRoom[i].timeLateOut.slice(0, 2) > 12 && dataRoom[i].timeLateOut !== "12:00") ? 40 : 0;
                        const earlyCheckInShorten = (Number(dataRoom[i].timeEarly.slice(0, 2)) > 15 
                                    && dataRoom[i].timeEarly !== "15:00") ? i===0 ? (checkMonthStartMonth ? 40: 0 ) : 40 : 0 // checkMonthStartMonth  ?: 0;
                        const earlyCheckInExtend = (Number(dataRoom[i].timeEarly.slice(0, 2)) < 15 && dataRoom[i].timeEarly !== "15:00") ? 40 : 0;
                       
                        if(earlyCheckInShorten === 40 && dataRoom[i].nights !== 0 && i > 0 ) { //&& i > 0
                            if( dataRoom[i - 1] ==="" || (dataRoom[i - 1].timeLateOut === "12:00" )) { //i === (data.listShowDate.length - 1) ||
                                result.push(<div //|| (dataRoom[i].night === 0 && lateOut!==0)
                                    className={`rsv-room ${isRealtimeNewCell ? 'rsv-cell-realtime-new' : ''}`}
                                    onClick={() => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                    style={{
                                            width: '40px',//checkWith2(),
                                            backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""
                                        }}>
                                        {(isCheckMode ? "Y1" : "")+String(i + 1).padStart(2, '0')}
                                </div>);
                            } else {
                                if(dataRoom[i - 1]) {
                                    if(Array.isArray(dataRoom[i - 1])) {
                                        if(dataRoom[i-1].length === 1) {
                                            if(dataRoom[i-1][0].timeLateOut.slice(0,2) <=18) {
                                                result.push(<div //|| (dataRoom[i].night === 0 && lateOut!==0)
                                                className={`rsv-room ${isRealtimeNewCell ? 'rsv-cell-realtime-new' : ''}`}
                                                onClick={() => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                                style={{
                                                        width: '40px',//checkWith2(),
                                                        backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""
                                                    }}>
                                                    {(isCheckMode ? "Y1x" : "")+String(i + 1).padStart(2, '0')}
                                            </div>);
                                            }
                                        }
                                    }
                                }
                            }

                        }

                        if(earlyCheckInShorten === 40 && dataRoom[i].nights !== 0 && i === 0) { // && Number(dataRoom[i + 1].timeEarly) === "15:00"  
                            result.push(<div //|| (dataRoom[i].night === 0 && lateOut!==0)
                            className={`rsv-room ${isRealtimeNewCell ? 'rsv-cell-realtime-new' : ''}`}
                            onClick={() => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                            style={{
                                    width:'40px',
                                    backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""
                                }}>
                                {(isCheckMode ? "Z" : "") +String(i + 1).padStart(2, '0') + "+" + dataRoom[i].name}
                           </div>);
                        } 

                        function checkWith() {
                            if(dataRoom[i].nights === 0) {
                                if(earlyCheckInShorten!==0) return 40;
                                if(earlyCheckInExtend !==0 && lateOut !== 0 ) return 80;
                                if(earlyCheckInExtend !==0 && lateOut === 40) return 80;
                            } 

                            if(dataRoom[i].nights === 1 && earlyCheckInShorten === 40) {
                                if(lateOut === 40) return 80;
                                return 40;
                            }
                            
                            if(i === 0 && earlyCheckInExtend  === 40) {
                                return (lengthRoom * 80 + lateOut - earlyCheckInShorten  )
                            }

                            if(dataRoom[i-1] && earlyCheckInExtend === 40) {



                                if(dataRoom[i-1] === "")  return (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend )
                                else {
                                    if(Array.isArray(dataRoom[i-1])) {
                                        if(dataRoom[i-1].length ===1) {
                                            if(dataRoom[i-1][0].timeLateOut !== "12:00") return (lengthRoom * 80 + lateOut - earlyCheckInShorten )
                                        } else {
                                            if(dataRoom[i-1][1].timeLateOut !== "12:00") return (lengthRoom * 80 + lateOut - earlyCheckInShorten )
                                        }
                                    } else {
                                        if("timeLateOut" in dataRoom[i-1] ) {

                                            if(dataRoom[i-1].timeLateOut !== "12:00") return (lengthRoom * 80 + lateOut - earlyCheckInShorten )
                                            if("timeEarly" in dataRoom[i-1] ) {                                                
                                                if (dataRoom[i-1].timeEarly.slice(0,2) > 15) return (lengthRoom * 80 + lateOut - earlyCheckInShorten );
                                                else if (dataRoom[i - 1].timeLateOut === "12:00" && earlyCheckInExtend === 40) return (lengthRoom * 80 + lateOut); 
                                                else return (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend )
                                            } else return (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend )
                                        } else {
                                            return (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend )
                                        }
                                    }
                                 }
                            }

                            // check i - 1 data have checkout 12:00-13:00
                            
                            if(dataRoom[i-1]) {
                                let checkTime
                                if(Array.isArray(dataRoom[i-1])) {
                                    if(dataRoom[i-1][dataRoom[i-1].length]) {
                                        if("timeLateOut" in dataRoom[i-1][dataRoom[i-1].length] ) checkTime = Number(dataRoom[i-1][dataRoom[i-1].length].timeLateOut.slice(0,2))
                                    }
                                } else {
                                    if("timeLateOut" in dataRoom[i-1] ){
                                        checkTime = Number(dataRoom[i-1].timeLateOut.slice(0,2))
                                    }
                                }

                                if(checkTime >12 && checkTime < 15) {
                                    return (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend -40)
                                 }
                                 
                            }

                            return (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend )
                        }

                        result.push(<div
                            className={`rsv-room active ${(isRealtimeNewCell || isRealtimeNewBooking) ? 'rsv-cell-realtime-new-block' : ''} ${
                                canDragBooking(branchID, auth.user.role, month, dataRoom[i]) && typeof dataRoom[i] === 'object' 
                                    ? 'booking-cell-draggable' 
                                    : ''
                            }`}
                            id={`cell-${roomNumber}-${i}`}
                            onClick={(e) => {
                                if (dragState.isDragging) {
                                    handleDrop(e, roomNumber, i);
                                    return;
                                }
                                window.open(`/room/${data.branchID}/${dataRoom[i].id}`, "_blank")
                            }}
                            onContextMenu={(e) => onContextMenu(e, roomNumber, i)}
                            onDragStart={(e) => {
                                if (dragState.isDragging) {
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('text/html', e.currentTarget);
                                }
                            }}
                            onDragOver={(e) => {
                                if (dragState.isDragging) {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                    handleDragOver(e, roomNumber, i);
                                }
                            }}
                            onDrop={(e) => {
                                if (dragState.isDragging) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDrop(e, roomNumber, i);
                                }
                            }}
                            onDragLeave={(e) => {
                                e.currentTarget?.classList.remove('drop-zone-valid', 'drop-zone-invalid', 'drop-zone-processing');
                            }}
                            draggable={canDragBooking(branchID, auth.user.role, month, dataRoom[i]) && typeof dataRoom[i] === 'object'}
                            {...bookingTooltipProps(dataRoom[i], roomNumber, i)}
                            data-booking-id={dataRoom[i].id}
                            style={{
                                backgroundColor: listRender ==="listWaiting" ? dataRoom[i].userBooking ? "#ea9999":"gray":
                                         dataRoom[i].status === "confirm" ? dataRoom[i].color : "black",
                                width:checkWith(),
                                cursor: canDragBooking(branchID, auth.user.role, month, dataRoom[i]) && typeof dataRoom[i] === 'object'
                                    ? 'grab'
                                    : 'default',
                                userSelect: 'none'
                            }}>

                            <div className="d-flex justify-content-between align-items-center"
                                >
                                <div className="d-flex  align-items-center" >
                                {/* {checkWith() >=80 && <FontAwesomeIcon
                                        className="mr-1"
                                        icon={(dataRoom[i].timeEarly !== "15:00" ) ? faCircleDot : ""} />  }
                                {(isCheckMode ? "X2" : "") + dataRoom[i].name} */}

                               {(checkWith() >=80 && dataRoom[i].timeEarly !== "15:00" ) ? <FontAwesomeIcon
                                        className="mr-1"
                                        icon={faCircleDot} />  : ""}
                                {(isCheckMode ? "X2" : "") + dataRoom[i].name}
                                      
                                </div>
                                {renderSmartIcons(dataRoom[i], listRender)}

                                {(checkWith() >=80 && dataRoom[i].timeLateOut !== "12:00" ) ? <FontAwesomeIcon icon={ faCircleDot } /> : ""}
                                
                            </div>

                            {!sharedBookingTooltip && !dragState.isDragging && <ReactTooltip
                                id={"data" + roomNumber + dataRoom[i].id+i}
                                className="bg_white"
                                border={false}
                                type="light"
                            >
                                <ReservationShowDetail
                                    showComponent={showComponent}
                                    roomNumber={roomNumber}
                                    show = {show}
                                    dataRender={dataRoom[i]}
                                />
                            </ReactTooltip>}

                            {id === (roomNumber + i) && show && (
                                // dataRoom[i].nights !== 0 ?
                                // <div style={{
                                //         width: '200px', backgroundColor: 'aqua',
                                //         color: 'back', position: 'absolute',
                                //         zIndex: 999, left: (points.x)
                                //     }}>
                                //     <ul><li
                                //             className="right_click_menu-item"
                                //             style={{ color: 'black', }}
                                //             onClick={() => window.open(`/room/${data.branchID}/${dataRoom[i].id}`, "_blank")}
                                //         >Open Edit</li>
                                //         <li className="right_click_menu-item" style={{ color: 'black', }}>Quick Edit</li>
                                //         <li className="right_click_menu-item" style={{ color: 'black', }}>Change Guest</li>
                                //         <li className="right_click_menu-item" style={{ color: 'black', }}>Cancel</li>
                                //     </ul>
                                // </div>   :
                                <div className="right_click_menu" >
                                    {/* <h5 style={{ textAlign: 'center', backgroundColor: 'yellow', color:'black' }}>{roomNumber + "(" + String(i + 1).padStart(2, '0') + ")"}</h5>
                                    <ul className="right_click_menu-list" >
                                        <li className="right_click_menu-item"
                                            style={{ color: 'black', }}
                                            onClick={() => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                        >Open Full Edit </li>
                                        <li className="right_click_menu-item" style={{ color: 'black', }}>Make Keep</li>
                                        <li className="right_click_menu-item" style={{ color: 'black', }}>Make Look</li>
                                    </ul> */}
                                </div>
                            )}

                        </div >)
                        
                    }
                }
                else if (Array.isArray(dataRoom[i])) {
                 //   console.log("dataRoom[i]", dataRoom[i]);
                    dataRoom[i].forEach((roomOnDataRoom, index)=> {
                        const isRealtimeNewBookingBlock = listRender === "listReservation" && roomOnDataRoom?.id && newRealtimeBookingIds.includes(roomOnDataRoom.id);
                        const checkTimeEarlyHour = Number(roomOnDataRoom.timeEarly.slice(0, 2))
                      //  console.log("roomOnDataRoom 234", roomOnDataRoom)
                       // const lengthRoom = calculateNightsAccSum(roomOnDataRoom.checkin, roomOnDataRoom.checkout, data.month)//roomOnDataRoom.nights;
                        const lateOut = (roomOnDataRoom.timeLateOut.slice(0, 2) > 12 && roomOnDataRoom.timeLateOut !== "12:00") ? 40 : 0;
                        const earlyCheckInShorten = ((checkTimeEarlyHour > 15 || (checkTimeEarlyHour === 15 && roomOnDataRoom.timeEarly.slice(3, 5) > 0) )
                                                         && roomOnDataRoom.timeEarly !== "15:00") ? 40 : 0;
                        const earlyCheckInExtend = (checkTimeEarlyHour < 15 && roomOnDataRoom.timeEarly !== "15:00") ? 40 : 0;

                        function renderHalfRoom(keyNote) {
                            return result.push(<div 
                                className= "rsv-room_half"
                                onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                onContextMenu={(e) => onContextMenu(e, roomNumber, i)}>
                                {/* {String(i + 1).padStart(2, '0')} */}
                                {(isCheckMode ? keyNote : "")+String(i + 1).padStart(2, '0')}
                                </div>)
                        }

                        if(index === 0) {
                            if(earlyCheckInShorten !== 0 && lateOut === 0) {
                                renderHalfRoom("K1.1")
                                // result.push(<div 
                                //     className= "rsv-room_half"
                                //     onClick={canNotEdit ? "" : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                //     style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                //     onContextMenu={(e) => onContextMenu(e)}>
                                //     {/* {String(i + 1).padStart(2, '0')} */}
                                //     {(isCheckMode ? "K1" : "")+String(i + 1).padStart(2, '0')}
                                //     </div>)
                            }

                            if(earlyCheckInShorten === 40 && lateOut === 40) {
                                if(dataRoom[i-1]) {
                                    if(dataRoom[i-1] === "") renderHalfRoom("K1.2")
                                     else {
                                        if(Array.isArray(dataRoom[i-1])) {
                                        } else if ("timeLateOut" in dataRoom[i-1]) {
                                            if(dataRoom[i-1].timeLateOut === "12:00") renderHalfRoom("K1.2")
                                        }
                                     }
                                } else {
                                    renderHalfRoom("K1.2")
                                }
                            } 
                        }

                       function checkNightZero(dataShort, dataExtend) {


                        //11fa80598d
                           if (dataRoom[i].length === 2 && (dataRoom[i][1].nights === 0 || dataRoom[i][1].nights === 1)) { 


                            if(dataRoom[i][1].nights === 0 && index === 1) return 40;
                            if(dataRoom[i][1].nights === 1 && index === 1) {
                                if(dataShort) {
                                    return 40;
                                } else if (lateOut === 40) {
                                    return roomOnDataRoom.nights * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend
                                } else {
                                  if(earlyCheckInShorten) return 40;
                                  else return 80;
                                }
                            }

                           return 40
                           }

                            if(roomOnDataRoom.nights >= 1) {

                                if(dataShort) {
                                    if(dataRoom[i].length === 2) {
                                        //&& dataExtend.id === roomOnDataRoom[i][1].id
                                        return roomOnDataRoom.nights * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend
                                    }
                                    if("timeLateOut" in dataShort && earlyCheckInShorten === 40) {
                                        return roomOnDataRoom.nights * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend - 40
                                    }
                                }
                                return roomOnDataRoom.nights * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend
                            } else {         
                                if(lateOut === 40 && (roomOnDataRoom.timeLateOut.slice(0, 2)) < 15) {
                                    return 40
                               //     const lateOut = (roomOnDataRoom.timeLateOut.slice(0, 2) > 12 && roomOnDataRoom.timeLateOut !== "12:00") ? 40 : 0;
                                } else {
                                    if( lateOut === 40 && earlyCheckInShorten === 40) return 40;
                                    if( lateOut === 40 && earlyCheckInExtend === 40) {
                                        if(dataShort) {
                                            if("timeLateOut" in dataShort) {
                                              if(dataExtend === "") return 80;
                                              else return 40;
                                            } else {
                                                return 80;
                                            }
                                        } else if (i === 0 && dataRoom[i][0].nights === 0) return 40;
                                        else return 80
                                    }
                                }

                            }
                            return 40;
                       }

                       const checkWith =checkNightZero( 
                            Array.isArray(dataRoom[i - 1]) ? dataRoom[i - 1][dataRoom[i - 1].length - 1] : dataRoom[i - 1], 
                            Array.isArray(dataRoom[i + 1]) ? dataRoom[i + 1][0] : dataRoom[i + 1], )

                        result.push(<div
                            // className={(dataRoom[i - 1] === "" || i === 0) ? "rsv-room active"
                            //     : (dataRoom[i - 1] === "" || i === 0) ? "rsv-room_half" : "rsv-room active"}
                            className={`rsv-room active ${(isRealtimeNewCell || isRealtimeNewBookingBlock) ? 'rsv-cell-realtime-new-block' : ''}`}
                            onClick={(e) => {
                                if (dragState.isDragging) {
                                    handleDrop(e, roomNumber, i);
                                    return;
                                }
                                window.open(`/room/${data.branchID}/${roomOnDataRoom.id}`, "_blank")
                            }}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                if (roomOnDataRoom && typeof roomOnDataRoom === 'object' && roomOnDataRoom.id) {
                                    handleBookingContextMenu(e, roomNumber, i, roomOnDataRoom);
                                    return;
                                }

                                if (dragState.isDragging) {
                                    cancelDrag();
                                    setShow(false);
                                    return;
                                }

                                onContextMenu(e, roomNumber, i);
                            }}
                            {...bookingTooltipProps(roomOnDataRoom, roomNumber, index, true)}
                            data-booking-id={roomOnDataRoom.id}
                            style={{
                                backgroundColor: roomOnDataRoom.status === "confirm" ? roomOnDataRoom.color : "black",
                                // width:(roomOnDataRoom.nights === 0 && earlyCheckInShorten!==0) ? 45 :
                                // (roomOnDataRoom.nights=== 0 && earlyCheckInExtend !==0 && lateOut !== 0 ) ? 80 :
                                // (roomOnDataRoom.nights === 0 && earlyCheckInExtend !==0 && lateOut === 40) ? 45 :
                                // (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend),
                                // width : roomOnDataRoom.nights === 0 ? checkNightZero(earlyCheckInShorten, lateOut) : 
                                // (lengthRoom * 80 + lateOut - earlyCheckInShorten + earlyCheckInExtend),
                                 width: checkWith //40
                            }}>

                            <div className="d-flex justify-content-between align-items-center ">
                                <div className="d-flex align-items-center">
                                {
                                    checkWith >=80 && <FontAwesomeIcon
                                    className="mr-1"
                                    icon={(roomOnDataRoom.timeEarly !== "15:00") ? faCircleDot : ""} />
                                }
                                    {(isCheckMode ? "Y2" : "") + roomOnDataRoom.name}
                                    {/* {roomOnDataRoom.name} */}
                                </div>
                                {renderSmartIcons(roomOnDataRoom, listRender)}

                                {
                                    checkWith >=80 && <FontAwesomeIcon icon={roomOnDataRoom.timeLateOut !== "12:00" ? faCircleDot : ""} />
                                }
                             
                            </div>
                            {!sharedBookingTooltip && !show && !dragState.isDragging && (<ReactTooltip
                                id={"data" + roomNumber + roomOnDataRoom.id + index}
                                type="light">
                                <ReservationShowDetail
                                    showComponent={showComponent}
                                    roomNumber={roomNumber}
                                    show={show}
                                    dataRender={roomOnDataRoom}
                                />
                              
                            </ReactTooltip>)}

                            {id === (roomNumber + i) && show && (
                                <div className="right_click_menu" >
                                    {/* <h5 style={{ textAlign: 'center', backgroundColor: 'yellow', color:'black' }}>{roomNumber + "(" + String(i + 1).padStart(2, '0') + ")"}</h5>
                                    <ul className="right_click_menu-list" >
                                        <li className="right_click_menu-item"
                                            style={{ color: 'black', }}
                                            onClick={() => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                        >Open Full Edit </li>
                                        <li className="right_click_menu-item" style={{ color: 'black', }}>Make Keep</li>
                                        <li className="right_click_menu-item" style={{ color: 'black', }}>Make Look</li>
                                    </ul> */}
                                </div>
                            )}

                        </div >)

                        if (dataRoom[i].length === 2 && index === 0 && roomOnDataRoom.nights === 0 && earlyCheckInExtend && Number(dataRoom[i][1].timeEarly.slice(0, 2)) > 15) {
                            result.push(<div 
                                className= "rsv-room_half"
                                onClick={canNotEdit ? "" : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                onContextMenu={(e) => onContextMenu(e)}
                                >
                                {(isCheckMode ? "Add3-" : "") + String(i + 1).padStart(2, '0')}
                            </div>)
                        }


                        // if night = 0 check full / half night
                        if(dataRoom[i].length > 1 && index ===1) {
                            //dataRoom[i - 1], dataRoom[i + 1]
                            if(lateOut === 0) {
                                if(earlyCheckInShorten === 0  && earlyCheckInExtend ===0) {
                                    // rsv-room_half
                                 }

                                 if(earlyCheckInExtend !== 0) {
                                    result.push(<div 
                                            className= "rsv-room"
                                            style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                            onContextMenu={(e) => onContextMenu(e)}
                                            >
                                            {(isCheckMode ? "X3" : "") + String(i + 1).padStart(2, '0')}
                                            {/* {String(i + 1).padStart(2, '0')} */}
                                           </div>)
                                 }
                            } else {
                                if(roomOnDataRoom.timeEarly === "15:00" || earlyCheckInExtend !==0) {
                                // Checkin normal + late out
                                    if(index !== 1) {
                                        result.push(<div 
                                            className= "rsv-room_half"
                                            style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                            onContextMenu={(e) => onContextMenu(e)}
                                            >
                                            {(isCheckMode ? "K2" : "") + String(i + 1).padStart(2, '0')}
                                            {/* {String(i + 1).padStart(2, '0')} */}
                                    </div>)
                                    }

                                }
                            }
                        } else { // have one room 
                            if(dataRoom[i+1] ==="" && dataRoom[i].length === 1 ) {
                                if(lateOut === 0) {
                                  //  console.log("vo case 2.1")
                                    if(earlyCheckInShorten === 0  && earlyCheckInExtend ===0) {
                                        // rsv-room_half
                                     }
    
    
                                     if(earlyCheckInExtend !== 0) {
                                        result.push(<div 
                                                className= "rsv-room"
                                                style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                                onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                                onContextMenu={(e) => onContextMenu(e)}
                                                >
                                                {/* {String(i + 1).padStart(2, '0')} */}
                                                {(isCheckMode ? "X" : "") + String(i + 1).padStart(2, '0')}
                                               </div>)
                                     }
                                } else {
                                    if((roomOnDataRoom.timeEarly === "15:00" || earlyCheckInExtend !==0)) {
                                    // Checkin normal + late out
                                   // console.log("vo case 2.2")
                                    result.push(<div 
                                        className= "rsv-room_half"
                                        onClick={canNotEdit ? ()=>{}  : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                        style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                        onContextMenu={(e) => onContextMenu(e)}>
                                            {(isCheckMode ? "K3" : "") + String(i + 1).padStart(2, '0')}
                                       </div>)
                                    }
                                }
                            } else if (dataRoom[i].length === 1 && (roomOnDataRoom.nights === 0 || roomOnDataRoom.nights === 1) 
                                        && roomOnDataRoom.timeEarly === "15:00" && lateOut === 40 ) {
                                    // check next Date Room have e
                                    const nextDay = Array.isArray(dataRoom[i + 1]) ? dataRoom[i + 1][0] : dataRoom[i + 1];

                                    if(nextDay) {
                                        
                                    }
                                if(dataRoom[i+1]) {
                                    if(Array.isArray(dataRoom[i+1])) {
                                        if(Number(dataRoom[i+1][0].timeEarly.slice(0,2) < 12)) {

                                        } else {
                                            result.push(<div 
                                                className= "rsv-room_half"
                                                onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                                style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                                onContextMenu={(e) => onContextMenu(e)}
                                                >
                                                {(isCheckMode ? "K4.1" : "") + String(i + 1).padStart(2, '0')}
                                               </div>)
                                        }
                                    } else {
                                        result.push(<div 
                                            className= "rsv-room_half"
                                            onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                            style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                            onContextMenu={(e) => onContextMenu(e)}
                                            >
                                            {(isCheckMode ? "K4.2" : "") + String(i + 1).padStart(2, '0')}
                                           </div>)
                                    }
                                } else {
                                    result.push(<div 
                                        className= "rsv-room_half"
                                        onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                        style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                        onContextMenu={(e) => onContextMenu(e)}
                                        >
                                        {(isCheckMode ? "K4.3" : "") + String(i + 1).padStart(2, '0')}
                                       </div>)
                                    }
                                }

                            else {

                                if(dataRoom[i].length !== 2) {
                                    if (data[i+1]) {
                                        if(dataRoom[i+1].timeEarly === "15:00") {
                                            if(roomOnDataRoom.nights !== 0) {
                                                result.push(<div
                                                    className= "rsv-room_half"
                                                    style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                                    onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                                    onContextMenu={(e) => onContextMenu(e)}>
                                                    {(isCheckMode ? "K51" : "") + String(i + 1).padStart(2, '0')}
                                                    {/* {String(i + 1).padStart(2, '0')} */}
                                                   </div>)
                                            } else {
                                                if(earlyCheckInExtend === 40 && lateOut === 40) {
                                                    renderHalfRoom("K1.3")
                                                }
                                            }
                                        }
                                    } else {
                                        if(earlyCheckInShorten === 0 && earlyCheckInExtend === 40 ) {
                                          //  if(dataRoom[i+1].timeEarly === "15:00")
                                          if(dataRoom[i].length !== 3) {
                                            result.push(<div
                                                className= {lateOut === 40 ? "rsv-room_half" : "rsv-room"}
                                                style={{backgroundColor: canNotEdit ? style.backgroundColor.oldDay : ""}}
                                                onContextMenu={(e) => onContextMenu(e)}
                                                onClick={canNotEdit ? ()=>{} : () => window.open(`/room/${data.branchID}/${roomNumber}/${data.month}-${String(i + 1).padStart(2, '0')}`, "_blank")}
                                                >
                                                {(isCheckMode ? "K52" : "") + String(i + 1).padStart(2, '0')}
                                                {/* {String(i + 1).padStart(2, '0')} */}
                                               </div>)
                                          }
                                        }

                                    }
                                }
                            }
                        }
                    })
                }
            };
        }

        return result;
    };

    return (
        <>
            <div className={listRender ==="listReservation" ? "d-flex" : "d-flex"}
                style={{paddingTop: (listRender ==="listReservation" && renderNumber === 1) ? '7.3px' : 'unset'}}>
                <div className="rsv">
                    <div div style={{ paddingTop: (listRender ==="listReservation" && renderNumber === 1) ? '26px' : '' }}>
                        {branchID && renderSchedule()}
                    </div>
                </div>
            </div>

            {sharedBookingTooltip && !dragState.isDragging && (
                <ReactTooltip
                    id={sharedTooltipId}
                    className="reservation-shared-tooltip"
                    type="light"
                    border={false}
                    getContent={bookingKey => {
                        const booking = tooltipBookings.get(bookingKey);
                        return booking ? (
                            <ReservationShowDetail showComponent={showComponent} dataRender={booking} />
                        ) : null;
                    }}
                />
            )}

            {spotlightRect && (() => {
                const r = spotlightRect;
                const arrowAbove = r.top > 40;
                return (
                    <>
                        <style>{`@keyframes rcmArrowBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(${arrowAbove ? '6px' : '-6px'}); } }`}</style>
                        {/* Spotlight: highlight the room, dim everything else */}
                        <div data-testid="room-menu-spotlight" style={{
                            position: 'fixed', top: r.top - 2, left: r.left - 2, width: r.width + 4, height: r.height + 4,
                            borderRadius: 4, border: '2px solid #ffeb3b',
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35), 0 0 14px 4px rgba(255,235,59,0.9)',
                            pointerEvents: 'none', zIndex: 9990
                        }} />
                        {/* Arrow pointing at the room */}
                        <div style={{
                            position: 'fixed',
                            top: arrowAbove ? r.top - 30 : r.bottom + 4,
                            left: r.left + r.width / 2 - 10,
                            fontSize: 22, lineHeight: '24px', color: '#ffeb3b',
                            textShadow: '0 0 4px #000', pointerEvents: 'none', zIndex: 9991,
                            animation: 'rcmArrowBounce 0.8s ease-in-out infinite'
                        }}>{arrowAbove ? '▼' : '▲'}</div>
                    </>
                );
            })()}

            {bookingMenu && (
                <div
                    className="right_click_menu"
                    ref={bookingMenuPlacement.ref}
                    onClick={(e) => e.stopPropagation()}
                    onContextMenu={(e) => { e.preventDefault(); setBookingMenu(null); }}
                    style={{ zIndex: 9999, background: '#fff', color: '#000', border: '1px solid #ccc', borderRadius: 4, minWidth: 160, ...bookingMenuPlacement.position }}>
                    <ul className="right_click_menu-list" style={{ listStyle: 'none', margin: 0, padding: '4px 0' }}>
                        <li className="right_click_menu-item" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', opacity: menuStep ? 0.35 : 1, transition: 'opacity .15s' }} onClick={() => onBookingMenuAction('open')}>Open Full View</li>
                        {!hasBlockedGuest(bookingMenu.booking) && <li className="right_click_menu-item" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', opacity: menuStep && menuStep !== 'color' ? 0.35 : 1, transition: 'opacity .15s' }} onClick={() => onBookingMenuAction('color')}>Change Color</li>}
                        {menuStep === 'color' && !hasBlockedGuest(bookingMenu.booking) && createPortal(
                            <div className="right_click_menu rcm-color-panel"
                                ref={colorMenuPlacement.ref}
                                role="group" aria-label="Choose color"
                                onClick={(e) => e.stopPropagation()}
                                onContextMenu={e => { e.preventDefault(); e.stopPropagation(); closeBookingMenu(); }}
                                style={{ ...colorMenuPlacement.position, zIndex: 2147483647 }}>
                                <div className="rcm-color-title" style={{ fontSize: 13, backgroundColor: '#f0a4dd' }}>Choose color</div>
                                {renderColorPalette()}
                                <div className="rcm-color-footer">
                                    <span className="rcm-color-preview" style={{ backgroundColor: pickedColor || 'transparent' }} />
                                    <input
                                        className="rcm-color-hex"
                                        value={pickedColor}
                                        maxLength={7}
                                        placeholder="hex"
                                        onChange={(e) => setPickedColor(e.target.value)}
                                    />
                                </div>
                                <div className="rcm-color-note" style={{ fontSize: 12 }}>* Assigning a color sets the status to "confirmed".</div>
                                <div className="rcm-color-actions">
                                    <button className="rcm-btn rcm-btn-confirm" disabled={!pickedColor || pickedColor === bookingMenu.booking.color}
                                        onClick={() => handleConfirmColor(bookingMenu.booking, pickedColor)}>✔ OK</button>
                                    <button className="rcm-btn rcm-btn-cancel" onClick={() => setMenuStep(null)}>✖</button>
                                </div>
                            </div>,
                            document.body
                        )}

                        <li className="right_click_menu-item" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', opacity: menuStep && menuStep !== 'extend_shorten' ? 0.35 : 1, transition: 'opacity .15s' }} onClick={() => onBookingMenuAction('extend_shorten')}>Extend / Shorten </li>
                        {menuStep === 'extend_shorten' && (
                            <li onClick={(e) => e.stopPropagation()} style={{ border: '1px solid #ccc', borderTop: 0, padding: 8, background: '#fff' }}>
                                <div style={{ fontSize: 13, color: '#f54aca', marginBottom: 6 }}>
                                    Current: {bookingMenu.booking.checkin} → {bookingMenu.booking.checkout}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <button
                                        className="rcm-btn rcm-btn-cancel"
                                        style={{ minWidth: 28, padding: '2px 6px' }}
                                        onClick={() => setExtendShortenNights(String(Math.max(0, Number(extendShortenNights || bookingMenu.booking.nights || 0) - 1)))}
                                    >−</button>
                                    <input
                                        type="number"
                                        min={0}
                                        style={{ width: 72, fontSize: 12, textAlign: 'center' }}
                                        value={extendShortenNights}
                                        onChange={(e) => setExtendShortenNights(e.target.value)}
                                    />
                                    <button
                                        className="rcm-btn rcm-btn-confirm"
                                        style={{ minWidth: 28, padding: '2px 6px' }}
                                        onClick={() => setExtendShortenNights(String(Math.max(0, Number(extendShortenNights || bookingMenu.booking.nights || 0) + 1)))}
                                    >+</button>
                                    <span style={{ fontSize: 12, color: '#444' }}>nights</span>
                                </div>
                                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                    <button
                                        className="rcm-btn rcm-btn-confirm"
                                        disabled={String(extendShortenNights) === String(bookingMenu.booking.nights ?? '') || extendShortenNights === ''}
                                        onClick={() => handleConfirmExtendShorten(bookingMenu.booking, Number(extendShortenNights))}
                                    >✔ Confirm</button>
                                    <button className="rcm-btn rcm-btn-cancel" onClick={() => setMenuStep(null)}>✖</button>
                                </div>
                            </li>
                        )}
                        <li className="right_click_menu-item" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', background: 'green', color: 'white', opacity: menuStep ? 0.35 : 1, transition: 'opacity .15s' }} onClick={() => onBookingMenuAction('schedule')}>Change Schedule</li>
                        <li className="right_click_menu-item" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', background:'red', color: 'white', opacity: menuStep && menuStep !== 'cancel' ? 0.35 : 1, transition: 'opacity .15s' }} onClick={() => onBookingMenuAction('cancel')}>X Cancel Schedule</li>
                    </ul>

                    {menuStep === 'cancel' && (
                        <div style={{ padding: 6, borderTop: '1px solid #ccc' }}>
                            <label style={{ fontSize: 12, display: 'block' }}>Requirement 2:</label>
                            <textarea
                                rows={3}
                                style={{ width: '100%', fontSize: 12 }}
                                value={cancelRequirement2}
                                onChange={(e) => setCancelRequirement2(e.target.value)}
                            />
                            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                <button className="btn btn-sm btn-danger" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', background: 'green', color: 'white' }} onClick={() => handleConfirmCancel(bookingMenu.booking, cancelRequirement2)}>Confirm Cancel</button>
                                <button className="btn btn-sm btn-success" style={{ padding: '6px 12px', border: '1px solid #ccc', cursor: 'pointer', background:'red', color: 'white' }} onClick={() => setMenuStep(null)}>Not Change</button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {scheduleModal && (() => {
                const b = scheduleModal.booking;
                const f = scheduleModal.form;
                console.log("b check", b)
                const setField = (key, value) => setScheduleModal(previous => {
                    const form = { ...previous.form, [key]: value };
                    const checkin = form.checkin ? new Date(`${form.checkin}T00:00:00Z`) : null;
                    if (key === 'checkin' || key === 'checkout') {
                        const checkout = form.checkout ? new Date(`${form.checkout}T00:00:00Z`) : null;
                        form.nights = checkin && checkout
                            ? (checkout.getTime() - checkin.getTime()) / 86400000
                            : '';
                    } else if (key === 'nights') {
                        if (checkin && value !== '' && Number.isInteger(Number(value)) && Number(value) >= 0) {
                            checkin.setUTCDate(checkin.getUTCDate() + Number(value));
                            form.checkout = Number.isNaN(checkin.getTime()) ? '' : checkin.toISOString().slice(0, 10);
                        } else {
                            form.checkout = '';
                        }
                    }
                    return { ...previous, form };
                });
                const fields = [
                    ['checkin', 'Check in', 'date', toDateInput(b.checkin)],
                    ['checkout', 'Check out', 'date', toDateInput(b.checkout)],
                    ['nights', 'Nights', 'number', b.nights],
                ];
                const changed = (key, orig) => String(f[key] ?? '') !== String(orig ?? '');
                const hasChange = fields.some(([k, , , o]) => changed(k, o)) || changed('other2', b.other2);
                return (
                    <div className="sch-overlay" onClick={() => { if (!scheduleSaving) setScheduleModal(null); }} onContextMenu={(e) => e.preventDefault()}>
                        <div className="sch-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="sch-header">
                                <div>
                                    <h5 className="sch-title">Change Schedule</h5>
                                    <div className="sch-sub">Review the current booking and set the new schedule</div>
                                </div>
                                <button className="sch-close" disabled={scheduleSaving} onClick={() => setScheduleModal(null)}>✕</button>
                            </div>

                            <div className="sch-body">
                                <div className="sch-guest">
                                    <div className="sch-avatar" style={{ background: b.color || '#2e7d32' }}>{(b.name || '?').charAt(0).toUpperCase()}</div>
                                    <div>
                                        <div className="sch-guest-name">{b.name}</div>
                                        <span className="sch-badge">Room {scheduleModal.roomNumber}</span>
                                    </div>
                                </div>

                                <div className="sch-section-label">Current schedule</div>
                                <div className="sch-current">
                                    <div><span>Check in</span><b>{b.checkin}</b></div>
                                    <div className="sch-arrow">→</div>
                                    <div><span>Check out</span><b>{b.checkout}</b></div>
                                    <div className="sch-nights"><b>{b.nights}</b><span>nights</span></div>
                                </div>

                                <div className="sch-section-label">New schedule</div>
                                <div className="sch-grid">
                                    {fields.map(([key, label, type, orig]) => (
                                        <div key={key} className="sch-field">
                                            <label htmlFor={`schedule-${key}`}>{label}{changed(key, orig) && <em className="sch-changed">changed</em>}</label>
                                            <input id={`schedule-${key}`} type={type} className={`sch-input ${changed(key, orig) ? 'is-changed' : ''}`} value={f[key]}
                                                onChange={(e) => setField(key, e.target.value)} />
                                        </div>
                                    ))}
                                </div>
                                <div className="sch-field" style={{ marginTop: 10 }}>
                                    <label>Requirement 2{changed('other2', b.other2) && <em className="sch-changed">changed</em>}</label>
                                    <textarea rows={3} className={`sch-input ${changed('other2', b.other2) ? 'is-changed' : ''}`} value={f.other2}
                                        onChange={(e) => setField('other2', e.target.value)} />
                                </div>
                            </div>

                            <div className="sch-footer">
                                {scheduleError && <div className="text-danger" role="alert">{scheduleError}</div>}
                                <button className="sch-btn sch-btn-cancel" disabled={scheduleSaving} onClick={() => setScheduleModal(null)}>Cancel</button>
                                <button className="sch-btn sch-btn-confirm" disabled={!hasChange || scheduleSaving} onClick={() => handleConfirmSchedule(b, f)}>{scheduleSaving ? 'Saving...' : '✔ Confirm change'}</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {cancelledBookingVisuals.length > 0 && createPortal(
                <div className="booking-cancel-effects" aria-hidden="true">
                    {cancelledBookingVisuals.map(({ text, ...visual }, index) => (
                        <div key={index} className="booking-cancel-dissolve" style={visual}>{text}</div>
                    ))}
                </div>,
                document.body
            )}

            {/* === Drag & Drop UI Components === */}
            {/* <DragPreview dragPreview={dragPreview} /> */}
            
            <DragConfirmDialog
                confirmDialog={confirmDialog}
                onConfirm={handleConfirmDragDrop}
                onCancel={cancelDrag}
            />

            <DragStatusIndicator
                status={dragStatus}
                message={
                    dragStatus === 'success'
                        ? 'Booking moved successfully'
                        : dragStatus === 'error'
                        ? 'Failed to move booking'
                        : ''
                }
                duration={3000}
            />
        </>
    );
}

export default ReservationScheduleComponent;
